import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Restaurant } from '@/queries/useRestaurants'
import { Edit, Search, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface RestaurantListProps {
  restaurants: Restaurant[] | undefined
  isLoading: boolean
  error: Error | null
  onEdit: (restaurant: Restaurant) => void
  onDelete: (restaurant: Restaurant) => void
}

export default function RestaurantList({
  restaurants,
  isLoading,
  error,
  onEdit,
  onDelete,
}: RestaurantListProps) {
  const { t } = useTranslation('admin')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Client-side filtering by name, email, phone (using debounced query)
  const filteredRestaurants = useMemo(() => {
    if (!restaurants) return []
    if (!debouncedSearchQuery.trim()) return restaurants

    const query = debouncedSearchQuery.toLowerCase().trim()
    return restaurants.filter(
      (restaurant) =>
        restaurant.name.toLowerCase().includes(query) ||
        restaurant.email?.toLowerCase().includes(query) ||
        restaurant.phone?.toLowerCase().includes(query)
    )
  }, [restaurants, debouncedSearchQuery])

  if (error) {
    return (
      <Alert variant="destructive" data-testid="restaurant-list-error">
        <AlertDescription>
          {t('restaurants.error.loadFailed')}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div 
      className="flex flex-col gap-[var(--spacing-md)]"
      data-testid="restaurant-list"
    >
      {/* Search Bar */}
      <div className="relative" data-testid="restaurant-list-search">
        <Search
          className="absolute left-[var(--space-4)] top-1/2 -translate-y-1/2 pointer-events-none z-10 w-4 h-4 text-[var(--text-secondary)] rtl:left-auto rtl:right-[var(--space-4)]"
        />
        <Input
          type="text"
          placeholder={t('restaurants.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-[var(--space-10)] rtl:pl-0 rtl:pr-[var(--space-10)]"
          data-testid="restaurant-list-search-input"
        />
      </div>

      {/* Results Count */}
      {debouncedSearchQuery && (
        <p
          className="text-[var(--text-sm)] text-[var(--text-secondary)]"
          data-testid="restaurant-list-results-count"
        >
          {t('restaurants.resultsCount', { count: filteredRestaurants.length })}
        </p>
      )}

      {/* Loading State */}
      {isLoading && (
        <div 
          className="flex flex-col gap-[var(--spacing-sm)]"
          data-testid="restaurant-list-loading"
        >
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredRestaurants.length === 0 && (
        <div
          className="text-center pt-[var(--spacing-2xl)] pb-[var(--spacing-2xl)] text-[var(--text-secondary)]"
          data-testid="restaurant-list-empty"
        >
          <p className="text-[var(--text-lg)] font-medium mb-[var(--spacing-sm)]">
            {t('restaurants.emptyState.title')}
          </p>
          <p className="text-[var(--text-sm)]">
            {debouncedSearchQuery
              ? t('restaurants.emptyState.searchMessage')
              : t('restaurants.emptyState.noDataMessage')}
          </p>
        </div>
      )}

      {/* Restaurant Table */}
      {!isLoading && filteredRestaurants.length > 0 && (
        <Card>
          <Table data-testid="restaurant-list-table">
            <TableHeader>
              <TableRow>
                <TableHead data-testid="restaurant-list-header-name">{t('restaurants.table.restaurant')}</TableHead>
                <TableHead data-testid="restaurant-list-header-email">{t('restaurants.table.email')}</TableHead>
                <TableHead data-testid="restaurant-list-header-phone">{t('restaurants.table.phone')}</TableHead>
                <TableHead className="text-left rtl:text-right" data-testid="restaurant-list-header-actions">
                  {t('restaurants.table.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRestaurants.map((restaurant) => (
                <TableRow key={restaurant.id} data-testid={`restaurant-row-${restaurant.id}`}>
                  <TableCell
                    className="font-medium"
                    data-testid={`restaurant-name-${restaurant.id}`}
                  >
                    {restaurant.name}
                  </TableCell>
                  <TableCell data-testid={`restaurant-email-${restaurant.id}`}>
                    {restaurant.email || '-'}
                  </TableCell>
                  <TableCell data-testid={`restaurant-phone-${restaurant.id}`}>
                    {restaurant.phone || '-'}
                  </TableCell>
                  <TableCell className="text-left rtl:text-right">
                    <div className="flex justify-start gap-[var(--spacing-sm)] rtl:justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(restaurant)}
                        data-testid={`restaurant-edit-button-${restaurant.id}`}
                      >
                        <Edit className="h-4 w-4 mr-[var(--spacing-xs)] rtl:mr-0 rtl:ml-[var(--spacing-xs)]" />
                        {t('common:buttons.edit')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(restaurant)}
                        data-testid={`restaurant-delete-button-${restaurant.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-[var(--spacing-xs)] rtl:mr-0 rtl:ml-[var(--spacing-xs)]" />
                        {t('common:buttons.delete')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  )
}

